import { h, Component } from "preact";
import { css } from "emotion";
import FullHeightFilterModal from "../../components/FullHeightFilterModal";
import FilterValueDisplay from "./FilterValueDisplay";
import locationIcon from "../../assets/images/icon-location.svg";
import dateIcon from "../../assets/images/icon-date.svg";
import filterIcon from "../../assets/images/icon-filter.svg";
import { hexToRgba } from "../../utils/Format";

const styles = {
  wrapper: css`
    width: 100%;
  `,
  desktop: css`
    @media (max-width: 600px) {
      display: none;
    }
  `,
  mobile: css`
    display: none;
    width: 100%;
    height: 36px;
    @media (max-width: 600px) {
      display: inline;
    }
  `,
  filterContainer: css`
    width: 100%;
    max-width: 410px;
    display: flex;
    @media (max-width: 600px) {
      flex-wrap: wrap;
    }
  `,
  filterButton: css`
    width: 100%;
    background: #fff;
    height: 36px;
    text-align: center;
  `,
  buttonContents: css`
    position: relative;
    width: 120px;
    margin-left: auto;
    margin-right: auto;
  `,
  filterIcon: css`
    position: absolute;
    left: 0px;
    vertical-align: middle;
  `,
  filterLabel: css`
    padding-left: 10px;
    display: inline-block;
  `,
  locationContainer: css`
    width: 100%;
    max-width: 250px;
    height: 36px;
    position: relative;
    @media (max-width: 600px) {
      width: 100%;
      max-width: 100%;
      margin-bottom: 8px;
    }
  `,
  dateContainer: css`
    width: 100%;
    max-width: 150px;
    height: 36px;
    margin-left: 8px;
    position: relative;
    @media (max-width: 600px) {
      width: 100%;
      max-width: 100%;
      margin-left: 0px;
    }
  `,
  icon: css`
    position: absolute;
    width: 20px;
    top: 9px;
    left: 10px;
  `,
  select: css`
    background: #fff;
    border: 1px solid #788391;
    border-radius: 4px !important;
    height: 1.8rem;
    line-height: 1rem;
    outline: none;
    padding: 0.35rem 0.4rem;
    vertical-align: middle;
    width: 100%;
    padding-left: 40px;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
  `
};

const Contents = props => {
  const {
    pageLayout,
    tourDates,
    locationOptions,
    dateOptions,
    selectFilter,
    mobile = false,
    displayDate,
    location,
    totalSize,
    theme
  } = props;

  const customStyles = {
    contentsContainer: css`
      width: 100%;
      max-width: 1000px;
      margin-top: ${pageLayout === "list" ? "32px" : "41px"};
      margin-bottom: 32px;
      height: 52px;
      display: flex;
      border-radius: 4px;
      background-color: ${mobile
        ? "#ffffff"
        : hexToRgba(theme.eventCardBackgroundColor, theme.cardAndBarOpacity)};
      padding-top: 8px;
      padding-left: 16px;
      padding-right: 16px;
      justify-content: space-between;
      @media (max-width: 600px) {
        margin-left: 14px;
        margin-right: 14px;
        width: auto;
        flex-wrap: wrap;
        margin-top: 8px;
        padding-left: 0px;
        padding-right: 0px;
      }
    `
  };

  return (
    <div className={customStyles.contentsContainer}>
      <FilterValueDisplay
        date={displayDate}
        location={location}
        totalSize={totalSize}
        theme={theme}
      />
      <div className={styles.filterContainer}>
        {tourDates.length > 1 ? (
          <div className={styles.locationContainer}>
            <select
              className={styles.select}
              onChange={e => selectFilter(e, "location", mobile)}
              id="select-tour-location"
            >
              {locationOptions.map(option => (
                <option value={option.value}>{option.label}</option>
              ))}
            </select>
            <img src={locationIcon} className={styles.icon} />
          </div>
        ) : null}

        <div className={styles.dateContainer}>
          {dateOptions.length > 1 ? (
            <select
              className={styles.select}
              onChange={e => selectFilter(e, "date", mobile)}
              id="select-tour-date"
            >
              {dateOptions.map(option => (
                <option value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : null}
          <img src={dateIcon} className={styles.icon} />
        </div>
      </div>
    </div>
  );
};

/**
 * Display filter by dates and locations
 *
 * @class Filter
 * @extends {Component}
 */
class Filter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalVisibility: false,
      displayDate: props.displayDate,
      location: props.location,
      date: props.date
    };
  }

  openModal = () => {
    this.setState({ modalVisibility: !this.state.modalVisibility });
  };

  mobileFilter = () => {
    const { filter } = this.props;
    const { date, displayDate, location } = this.state;

    const mobileUpdate = { date, displayDate, location };
    filter(null, null, mobileUpdate);
    this.openModal();
  };

  selectFilter = (e, dateOrLocation, mobile) => {
    const { filter } = this.props;

    if (mobile) {
      const update = {};
      if (dateOrLocation === "date") {
        const index = e.nativeEvent.target.selectedIndex;
        const text = e.nativeEvent.target[index].text;
        update.displayDate = text;
      }
      update[dateOrLocation] = e.target.value;
      this.setState(update);
    } else {
      filter(e, dateOrLocation);
    }
  };

  render() {
    const {
      pageLayout,
      tourDates = [],
      tourLocations = [],
      clearFilter,
      displayDate,
      location,
      totalSize,
      theme
    } = this.props;
    const { modalVisibility } = this.state;

    const hideFilter = !(tourDates.length > 1 || tourLocations.length > 1);
    const dateOptions = tourDates.map(option => {
      return { value: option.value, label: option.display };
    });
    dateOptions.unshift({ value: "all", label: "All Dates" });
    const locationOptions = tourLocations.map(option => {
      return { value: option, label: option };
    });
    locationOptions.unshift({ value: "all", label: "All Locations" });

    if (hideFilter) {
      return <div style={{ marginTop: 48 }} />;
    }

    return (
      <div className={styles.wrapper}>
        <div className={styles.desktop}>
          <Contents
            pageLayout={pageLayout}
            tourDates={tourDates}
            locationOptions={locationOptions}
            dateOptions={dateOptions}
            selectFilter={this.selectFilter}
            displayDate={displayDate}
            location={location}
            totalSize={totalSize}
            theme={theme}
          />
        </div>
        <div className={styles.mobile}>
          <button className={styles.filterButton} onClick={this.openModal}>
            <div className={styles.buttonContents}>
              <img src={filterIcon} className={styles.filterIcon} />
              <div className={styles.filterLabel}>Filter events</div>
            </div>
          </button>
          <FullHeightFilterModal
            showModal={modalVisibility}
            closeHandler={this.openModal}
            title="Filter tours"
            maxWidth={100}
            mobileFilter={this.mobileFilter}
            clearFilter={clearFilter}
            contents={
              <Contents
                pageLayout={pageLayout}
                tourDates={tourDates}
                locationOptions={locationOptions}
                dateOptions={dateOptions}
                selectFilter={this.selectFilter}
                theme={theme}
                mobile={true}
              />
            }
          />
        </div>
      </div>
    );
  }
}

export default Filter;



// WEBPACK FOOTER //
// ./routes/tours/Filter.js